import { last, get, split } from 'lodash'
import React from 'react'
import styled from 'styled-components'
import { IconBbf, IconCheck } from 'blocket-ui'
import ToolTip from 'components/ToolTip'
import { TextWrapper, Text, Title, sharedToolTipStyles } from 'components/ToolTip/ToolTipStyles.js'
import { mediaQueries } from '@blazar-plugins/device'
import { toObject } from 'utils/parameterGroups'
import ImageBadge from 'components/ImageBadge'
import { shouldLinkAdToInfoPage } from 'utils/ad'
import { CATEGORIES } from 'utils/constants'

const ToolTipWrapper = styled.div`
  ${sharedToolTipStyles}
  top: -5px; /* Mobile */
  left: 35px; /* Mobile */
  &:after {
    content: '';
    width: 12px;
    height: 12px;
    top: 10px;
    left: -7px;
    -webkit-transform: rotate(-135deg);
    -ms-transform: rotate(-135deg);
    transform: rotate(-135deg);

    @media ${mediaQueries.extraLarge} {
      width: 13px;
      height: 13px;
      left: 124px;
      top: -8px;
      -webkit-transform: rotate(-45deg);
      -ms-transform: rotate(-45deg);
      transform: rotate(-45deg);
    }
  }
  @media ${mediaQueries.extraLarge} {
    max-width: 280px;
    top: 36px;
    left: -120px; /*  Desktop */
  }
`

const categoryMappers = {
  // Everything else
  default: (ad) => {
    const image = get(ad, 'images[0]')

    return {
      image,
    }
  },
  [CATEGORIES.MOTOR]: (ad, device, isPremiumTooltipEnabled) => {
    const isCar = ad.category && ad.category.some((category) => category.id === '1020')
    const adData = toObject(ad.parameterGroups)
    const regDate = get(adData, 'general.regdate')
    const fuel = get(adData, 'general.fuel')
    const milage = get(adData, 'general.mileage')
    const gearbox = get(adData, 'general.gearbox')
    const isPremium =
      get(ad, 'featuredPlacements', []).some((itr) => itr.id === 'premium_warranty') ||
      get(ad, 'premiumPlacement')
    const premiumWarrentyLabel = get(ad, 'featuredPlacements[0].shortLabel')

    const insuranceText = [
      'Tryggt för köpare & säljare',
      '6 mån. skydd mot fel på bilen',
      'Omfattar motor, växellåda, airbags',
    ]

    const textArray = isPremium
      ? split(get(ad, 'featuredPlacements[0].text', ''), ',')
      : insuranceText

    const ToolTipTitle = () => (
      <Title>{isPremium ? premiumWarrentyLabel : 'Begagnatförsäkring ingår'}</Title>
    )
    const ToolTipText = () => {
      return textArray.map((text) => (
        <TextWrapper key={text}>
          <IconCheck width={16} height={16} color="#49b975" />
          <Text>{text}</Text>
        </TextWrapper>
      ))
    }
    const image = get(ad, 'images[0]')

    const imageBadges = []

    const icon = isPremiumTooltipEnabled ? (
      <ToolTip
        style={{ position: 'initial' }}
        ToolTipWrapper={ToolTipWrapper}
        title={<ToolTipTitle />}
        text={<ToolTipText />}
      >
        <IconBbf width={24} height={24} />
      </ToolTip>
    ) : (
      <IconBbf width={24} height={24} />
    )
    if (get(ad, 'parametersRaw.motorDirectPurchase.value') === '1') {
      imageBadges.push(
        <ImageBadge key="labelImageBadge" text="Köp online" type="label" position="bottomLeft" />,
      )
    }

    if (get(adData, 'general.insurance.value') === '1' || isPremium) {
      imageBadges.push(
        <ImageBadge key="iconImageBadge" type="icon" position="topLeft" icon={icon} />,
      )
    }

    return {
      image,
      imageBadges,
      adParams: [
        regDate && regDate.value,
        isCar && fuel && fuel.value,
        isCar && milage && `${milage.value} mil`,
        isCar && gearbox && gearbox.value,
        isCar && premiumWarrentyLabel,
      ].filter(Boolean), // Items may be undefined so lets remove them
    }
  },
  [CATEGORIES.REAL_ESTATE]: (ad, device) => {
    const image = get(ad, 'images[0]')

    const adData = toObject(ad.parameterGroups)
    const rooms = get(adData, 'residence.rooms')
    const size = get(adData, 'residence.size')
    const monthlyRent = get(adData, 'residence.monthlyRent')
    const imageBadges = []

    const adParams = [
      !device.is('medium') && last(ad.category).name,
      rooms && `${rooms.value} Rum`,
      monthlyRent && `${monthlyRent.value} kr/mån`,
      size && `${size.value} ${size.suffix}`,
    ]

    const infopageUrl = get(ad, 'infopage.url')

    if (infopageUrl && infopageUrl.includes('trygg-uthyrning=1')) {
      imageBadges.push(
        <ImageBadge
          key="labelImageBadge"
          text="Trygg uthyrning"
          type="label"
          position="bottomLeft"
        />,
      )
    }

    return {
      image,
      imageBadges,
      adParams: adParams.filter(Boolean),
    }
  },
  [CATEGORIES.EMPLOYMENT]: (ad) => {
    const imageSrc = get(ad, 'images[0].url')

    return {
      isExternalLink: true,
      imageSrc,
    }
  },
}

export default function getExtraProps(ad, device, isPremiumTooltipEnabled) {
  let categoryMapper = categoryMappers.default

  ad.category.forEach(({ id }) => {
    if (categoryMappers[id]) {
      categoryMapper = categoryMappers[id]
    }
  })

  const extraProps = categoryMapper(ad, device, isPremiumTooltipEnabled)

  if (shouldLinkAdToInfoPage(ad)) {
    extraProps.url = get(ad, 'infopage.url')
  }

  return extraProps
}
